import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Alert, Box, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { getVideoBooking } from '../api';
import { useEnv } from '../contexts/EnvContext';
import PageHeader from './common/PageHeader';
import LoadingBlock from './common/LoadingBlock';
import ErrorBlock from './common/ErrorBlock';
import StuckClocks from './StuckClocks';
import CallTimeline from './CallTimeline';
import RecordingList from './RecordingList';

const when = (d) => (d ? dayjs(d).format('MMM D, YYYY h:mm A') : '—');
const tokens = (n) => (n == null ? '—' : Number(n).toLocaleString());
const text = (v) => (v == null || v === '' ? '—' : String(v));

// One table of rows, each column a [heading, cell] pair; says so when there are none.
export function Rows({ title, rows, columns, empty }) {
  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
      {rows.length ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label={title}>
            <TableHead>
              <TableRow>{columns.map(([heading]) => <TableCell key={heading}>{heading}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  {columns.map(([heading, cell]) => (
                    <TableCell key={heading} sx={{ maxWidth: 320, wordBreak: 'break-word' }}>{cell(row)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">{empty}</Typography>
      )}
    </Paper>
  );
}

export function Fact({ label, children }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{children}</Typography>
    </Grid>
  );
}

/**
 * One video booking in full for staff (admin panel plan RPT-09): the booking
 * half of Bidsy's route 24. The panel shows what Bidsy answers and works out
 * nothing.
 */
export default function VideoBooking() {
  const { connectionId } = useParams();
  const location = useLocation();
  const { env } = useEnv();
  const from = location.state?.from || { label: 'Connections', path: '/connections' };
  const [state, setState] = useState({ loading: true, data: null, error: null, notice: null });
  const latest = useRef(0);

  const load = useCallback(async ({ quiet = false } = {}) => {
    const asked = ++latest.current;
    if (!quiet) setState({ loading: true, data: null, error: null, notice: null });
    try {
      const answer = await getVideoBooking(connectionId);
      if (asked !== latest.current) return;
      setState({ loading: false, data: answer.data, error: null, notice: null });
    } catch (e) {
      if (asked !== latest.current) return;
      const body = e?.response?.data;
      // Not deployed, or no booking with this number, is an answer to show, not a failure to retry.
      if (e?.response?.status === 404) setState({ loading: false, data: null, error: null, notice: body?.message || 'No video booking has this number.' });
      else setState({ loading: false, data: null, error: body?.message || e?.message || 'Could not load the booking', notice: null });
    }
  }, [connectionId]);

  useEffect(() => { load(); }, [load, env]);

  const header = (
    <PageHeader
      title={`Video call #${connectionId}`}
      subtitle={<Link to={from.path}>{from.label}</Link>}
    />
  );
  if (state.loading) return <>{header}<LoadingBlock height={320} /></>;
  if (state.error) return <>{header}<ErrorBlock message={state.error} onRetry={load} /></>;
  if (state.notice) return <>{header}<Alert severity="info">{state.notice}</Alert></>;

  const { booking, fan, creator } = state.data;
  const person = (p) => (p ? (
    <Link to={`/user/${p.id}`} state={{ from: { label: `Video call #${booking.id}`, path: `/video-bookings/${booking.id}` } }}>{p.username}</Link>
  ) : '—');

  return (
    <>
      {header}
      <StuckClocks connectionId={booking.id} onRan={() => load({ quiet: true })} />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Fact label="Fan">{person(fan)}</Fact>
          <Fact label="Creator">{person(creator)}</Fact>
          <Fact label="State">{text(booking.state)}</Fact>
          <Fact label="How it was booked">{booking.origin === 'auction' ? 'Auction win' : 'Fixed price'}</Fact>
          <Fact label="Price">{tokens(booking.price_tokens)} tokens</Fact>
          <Fact label="Call length">{booking.call_length_minutes} minutes</Fact>
          <Fact label="Requested">{when(booking.requested_at)}</Fact>
          <Fact label="Answer due">{when(booking.answer_due_at)}</Fact>
          <Fact label="Ended">
            {when(booking.ended_at)}
            {booking.ending_code ? ` · ${booking.ending_code}${booking.ending_cause ? ` (${booking.ending_cause})` : ''}${booking.ended_by ? ` by ${booking.ended_by}` : ''}` : ''}
          </Fact>
          {booking.decline_reason_code ? (
            <Fact label="Decline reason (private)">
              {text(booking.decline_reason_label)}
              {booking.decline_note ? ` · “${booking.decline_note}”` : ''}
            </Fact>
          ) : null}
          {booking.cancel_note ? <Fact label="Cancel note">“{booking.cancel_note}”</Fact> : null}
          <Fact label="Pair block">{state.data.pair_block ? `Blocked from each other (case #${state.data.pair_block.case_id})` : 'None'}</Fact>
        </Grid>
      </Paper>

      <Rows
        title="Calls"
        rows={state.data.calls || []}
        empty="No call yet."
        columns={[
          ['Call', (c) => `#${c.id}`],
          ['Time', (c) => when(c.starts_at)],
          ['State', (c) => text(c.state)],
          ['Ending', (c) => text(c.ending_code)],
          ['Paid by', (c) => text(c.funded_by)],
          ['Source', (c) => text(c.source)],
        ]}
      />
      {(state.data.calls || []).map((call) => (
        <Paper key={`call-${call.id}`} sx={{ p: 2, mb: 2 }} data-testid={`call-${call.id}`}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Call #{call.id}: {when(call.starts_at)}, {text(call.state)}{call.ending_code ? `, ${call.ending_code}` : ''}
          </Typography>
          <CallTimeline timeline={call.timeline} totals={call.totals} />
          {(call.cases || []).length ? (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Cases:{' '}
              {call.cases.map((kase) => (
                <Link key={kase.id} to={`/video-cases/${kase.id}`} style={{ marginRight: 8 }}>#{kase.id} ({kase.kind}, {kase.state})</Link>
              ))}
            </Typography>
          ) : null}
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Joining</Typography>
          {(call.join_attempts || []).length ? (
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {call.join_attempts.map((a) => (
                <li key={a.id}>
                  <Typography variant="body2">
                    {when(a.requested_at)} · {a.side || `user #${a.user_id}`} · {text(a.outcome)}
                    {a.refusal_code ? ` · ${a.refusal_code}` : ''}{a.server_error ? ` · ${a.server_error}` : ''}
                    {a.browser_reported_at ? ` · the browser said: ${text(a.browser_outcome)}: ${text(a.browser_detail)}` : ''}
                  </Typography>
                </li>
              ))}
            </Box>
          ) : <Typography variant="body2" color="text.secondary">No join attempt recorded.</Typography>}
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Event log</Typography>
          {(call.events || []).length ? (
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {call.events.map((e) => (
                <li key={e.id}>
                  <Typography variant="body2">
                    {(Number(e.at_ms) / 1000).toFixed(1)} s · {e.identity || '—'} {e.kind}{e.reason ? ` (${e.reason})` : ''} · {e.source}
                  </Typography>
                </li>
              ))}
            </Box>
          ) : <Typography variant="body2" color="text.secondary">No event.</Typography>}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Recordings</Typography>
          <RecordingList recordings={call.recordings || []} call={call} fan={fan} creator={creator} />
        </Paper>
      ))}
      <Rows
        title="Asks and offers"
        rows={state.data.asks || []}
        empty="No ask to move and no offer."
        columns={[
          ['Kind', (a) => `${a.kind}${a.offer_kind ? ` (${a.offer_kind})` : ''}`],
          ['State', (a) => `${text(a.state)}${a.close_reason ? ` · ${a.close_reason}` : ''}`],
          ['Sent', (a) => `${when(a.sent_at)}${a.sent_by ? ` by ${a.sent_by}` : ''}`],
          ['Asked / picked time', (a) => `${when(a.asked_starts_at)} / ${when(a.picked_starts_at)}`],
          ['Due', (a) => `${when(a.answer_due_at)}${a.confirm_due_at ? ` · confirm ${when(a.confirm_due_at)}` : ''}`],
          ['Fan note', (a) => text(a.fan_note)],
          ['Creator note', (a) => text(a.creator_note)],
        ]}
      />
      <Rows
        title="Payments"
        rows={state.data.payments || []}
        empty="No payment."
        columns={[
          ['Payment', (p) => `#${p.id}`],
          ['Payer', (p) => (p.payer === 'bidsy' ? 'Bidsy' : 'Fan')],
          ['Tokens', (p) => tokens(p.amount)],
          ['State', (p) => `${text(p.state)}${p.frozen_by_case_id ? ` · frozen by case #${p.frozen_by_case_id}` : ''}`],
          ['Release', (p) => (p.paid_at ? `paid ${when(p.paid_at)}` : when(p.release_at))],
          ['Ledger ids', (p) => [p.debit_tx_id, p.outcome_tx_id].filter(Boolean).join(', ') || '—'],
        ]}
      />
      <Rows
        title="Clocks"
        rows={state.data.clocks || []}
        empty="No clock."
        columns={[
          ['Clock', (k) => `${k.kind} #${k.id}`],
          ['What it does', (k) => text(k.does)],
          ['State', (k) => text(k.state)],
          ['Due', (k) => when(k.due_at)],
          ['Tries', (k) => k.attempts],
          ['Last error', (k) => text(k.last_error)],
          ['Run again by', (k) => (k.retried_by ? `${k.retried_by}, ${when(k.retried_at)}` : '—')],
        ]}
      />
      <Rows
        title="Consent"
        rows={state.data.consents || []}
        empty="No consent recorded."
        columns={[
          ['Side', (c) => c.side],
          ['Rules version', (c) => text(c.rules_version)],
          ['Agreed', (c) => when(c.agreed_at)],
          ['Where', (c) => text(c.source)],
        ]}
      />
      <Rows
        title="Shows up for"
        rows={state.data.shows_up_for || []}
        empty="No record for this booking."
        columns={[
          ['Call', (r) => `#${r.call_id}`],
          ['Ending', (r) => text(r.ending_code)],
          ['Counts as', (r) => (r.class === 'miss' ? 'A miss' : 'Showed up')],
        ]}
      />
      <Rows
        title="History"
        rows={state.data.audit || []}
        empty="Nothing done by staff yet."
        columns={[
          ['When', (a) => when(a.created_at)],
          ['What', (a) => text(a.action)],
          ['By', (a) => text(a.actor)],
          ['Note', (a) => text(a.note)],
        ]}
      />
    </>
  );
}
